/* eslint-disable max-classes-per-file */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chart from 'react-google-charts';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from './ThemeContext';

import { renderBoilerPlate, getApiHost, fetchApiRequest, isProd } from './Common';
import { UserContext } from './UserContext';
// https://github.com/hackingbeauty/react-mic

interface LiveMicState {
  record: boolean;
  pause: boolean;
  saveAudio: boolean;
  noSampling: boolean;
}

interface EventPrediction {
  name: string;
  score: number;
}

interface EventPredictionsState {
  predictions: Array<Record<string, any>>;
  eventColors: string[];
}

interface EventData {
  [key: string]: any;
  Time?: Date;
}

interface AudioHistoryEntry {
  fileName: string;
  playTime: Date;
  duration?: number;
  status: 'played' | 'paused' | 'stopped';
}

let eventPredictions: any = null;

// Shared audio context and state for mixing microphone and uploaded audio
let sharedAudioContext: AudioContext | null = null;
let sharedDestination: MediaStreamAudioDestinationNode | null = null;
let sharedAudioChunks: Blob[] = [];
let sharedPredictionReady = true;
let sharedPredictUrl = '';

const EventPredictions: React.FC = () => {
  const { darkMode } = useTheme();
  const [state, setState] = useState<EventPredictionsState>({
    predictions: [],
    eventColors: [],
  });

  const eventNamesRef = useRef<string[]>(['Time']);
  const colorMapRef = useRef<Record<string, string>>({
    Casual: '#44FF44',
    Gunshot: '#FF0000',
    Explosion: '#AA0000',
    'Glass break': '#FFAA00',
    Thunder: '#0000FF',
    Siren: '#AAAAFF',
    Raining: '#4444AA',
    'Human activity': '#4444FF',
    'Human speech': '#0044AA',
    'Baby cry': '#00AAFF',
  });
  const maxPredictionsRef = useRef<number>(30);

  useEffect(() => {
    eventPredictions = {
      addEventPredictions: (events: EventPrediction[]) => {
        eventNamesRef.current = eventNamesRef.current
          .concat(events.map((e) => e.name))
          .filter((v, i, s) => s.indexOf(v) === i);

        const eventColors = eventNamesRef.current
          .slice(1)
          .map((n) => colorMapRef.current[n] || 'black');

        const eventsObj: EventData = events.reduce(
          (o, e) => ({ ...o, [e.name]: e.score }),
          {},
        );
        eventsObj.Time = new Date();

        setState(prevState => {
          let predictions = prevState.predictions.concat([eventsObj]);
          if (predictions.length >= maxPredictionsRef.current)
            predictions = predictions.slice(predictions.length - maxPredictionsRef.current);
          return { predictions, eventColors };
        });
      },
      eventNames: eventNamesRef.current,
    };
  }, []);

  const buildChartData = () => {
    const { predictions } = state;
    const data = [eventNamesRef.current].concat(
      predictions.map((p) => eventNamesRef.current.map((e) => p[e] || 0)),
    );
    return data;
  };

  const { predictions, eventColors } = state;
  if (predictions.length === 0) return null;

  // Calculate the sliding window overlay
  const chartWidth = 642;
  const chartAreaWidth = chartWidth * 0.85; // 85% of chart width
  const slidingWindowWidth = chartAreaWidth * (3 / 30); // 3 seconds out of 30 max predictions
  const slidingWindowPosition = chartAreaWidth - slidingWindowWidth; // Position at the right edge

  return (
    <Paper>
      <Typography variant="subtitle1">Live event predictions</Typography>
      <Typography variant="body2" sx={{ marginBottom: '8px', color: 'text.secondary' }}>
        Gray area shows 3-second sliding window used for latest prediction
      </Typography>
      <div style={{ position: 'relative' }}>
        <Chart
          chartType="AreaChart"
          data={buildChartData()}
          options={{
            legend: {
              position: 'top',
              maxLines: 3,
              textStyle: {
                color: darkMode ? '#ffffff' : '#000000'
              }
            },
            isStacked: 'percent',
            enableInteractivity: true,
            connectSteps: false,
            height: 300,
            width: 642,
            hAxis: {
              format: 'mm:ss',
              maxTextLines: 1,
              textStyle: {
                color: darkMode ? '#ffffff' : '#000000'
              }
            },
            vAxis: {
              textStyle: {
                color: darkMode ? '#ffffff' : '#000000'
              }
            },
            colors: eventColors,
            backgroundColor: 'transparent',
            chartArea: {
              backgroundColor: 'transparent',
              width: '85%',
              height: '80%'
            }
          }}
        />
        {/* Sliding window overlay */}
        <div
          style={{
            position: 'absolute',
            top: '30px', // Adjust to match chart area top
            left: `${slidingWindowPosition + 50}px`, // Adjust for chart margins
            width: `${slidingWindowWidth}px`,
            height: '240px', // Adjust to match chart area height
            backgroundColor: 'rgba(128, 128, 128, 0.2)',
            border: '1px solid rgba(128, 128, 128, 0.4)',
            pointerEvents: 'none',
            zIndex: 1
          }}
        />
      </div>
    </Paper>
  );
};

class Live extends React.Component<any> {
  static contextType = UserContext;

  render() {
    const user = this.context as any;
    
    if (MediaRecorder.isTypeSupported('audio/webm')) {
      return renderBoilerPlate(
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <LiveMic context={user} />
          <EventPredictions />
        </div>
          <AudioFileUpload />
        </div>,
      );
    }
    return renderBoilerPlate(
      <Alert elevation={6} variant="filled" severity="error">
        ERROR: Your browser does not support audio/webm format. Please try with a
        a browser like Firefox instead.
      </Alert>,
    );
  }
}

const AudioFileUpload: React.FC = () => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(-1);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [audioHistory, setAudioHistory] = useState<AudioHistoryEntry[]>(() => {
    // Load history from localStorage on component mount
    const saved = localStorage.getItem('audioHistory');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Convert string dates back to Date objects
        return parsed.map((entry: any) => ({
          ...entry,
          playTime: new Date(entry.playTime)
        }));
      } catch (error) {
        console.error('Error loading audio history:', error);
        return [];
      }
    }
    return [];
  });
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioSourceRef = useRef<MediaElementAudioSourceNode | null>(null);

  // Save history to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('audioHistory', JSON.stringify(audioHistory));
  }, [audioHistory]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const audioFiles = files.filter(file => file.type.startsWith('audio/'));

    if (audioFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...audioFiles]);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const audioFiles = files.filter(file => file.type.startsWith('audio/'));

    if (audioFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...audioFiles]);
    }
  };

  const playFile = (index: number) => {
    if (index < 0 || index >= uploadedFiles.length) return;

    // Stop current playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioSourceRef.current) {
      audioSourceRef.current.disconnect();
      audioSourceRef.current = null;
    }

    const file = uploadedFiles[index];
    const url = URL.createObjectURL(file);

    audioRef.current = new Audio(url);
    audioRef.current.loop = false;

    // Connect to shared audio context for mixing
    if (sharedAudioContext && sharedDestination) {
      audioSourceRef.current = sharedAudioContext.createMediaElementSource(audioRef.current);
      // Connect to shared destination for mixing with microphone
      audioSourceRef.current.connect(sharedDestination);
      // Also connect to speakers for audible playback
      audioSourceRef.current.connect(sharedAudioContext.destination);
    }

    audioRef.current.play();
    setCurrentFileIndex(index);
    setIsPlaying(true);
    setIsPaused(false);

    // Add to history
    const historyEntry: AudioHistoryEntry = {
      fileName: file.name,
      playTime: new Date(),
      status: 'played'
    };

    // Update local state
    setAudioHistory(prev => [historyEntry, ...prev.slice(0, 49)]); // Keep last 50 entries

    audioRef.current.onended = () => {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentFileIndex(-1);
    };
  };

  const pauseFile = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPaused(true);

      // Add pause to history
      if (currentFileIndex >= 0) {
        const file = uploadedFiles[currentFileIndex];
        const historyEntry: AudioHistoryEntry = {
          fileName: file.name,
          playTime: new Date(),
          status: 'paused'
        };

        // Update local state
        setAudioHistory(prev => [historyEntry, ...prev.slice(0, 49)]);
      }
    }
  };

  const resumeFile = () => {
    if (audioRef.current && isPaused) {
      audioRef.current.play();
      setIsPaused(false);
    }
  };

  const stopFile = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setIsPaused(false);

      // Add stop to history
      if (currentFileIndex >= 0) {
        const file = uploadedFiles[currentFileIndex];
        const historyEntry: AudioHistoryEntry = {
          fileName: file.name,
          playTime: new Date(),
          status: 'stopped'
        };

        // Update local state
        setAudioHistory(prev => [historyEntry, ...prev.slice(0, 49)]);
      }

      setCurrentFileIndex(-1);
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    if (currentFileIndex === index) {
      stopFile();
    } else if (currentFileIndex > index) {
      setCurrentFileIndex(prev => prev - 1);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('sv-SE', {
      timeZone: 'Europe/Stockholm',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'played': return '▶️';
      case 'paused': return '⏸️';
      case 'stopped': return '⏹️';
      default: return '📄';
    }
  };

  return (
    <Paper style={{ maxWidth: '42rem' }}>
      <Typography variant="subtitle1">Audio File Upload</Typography>
      <Typography variant="body2">
        Upload audio files to mix with live microphone audio. Files will be played through the same audio context as the microphone.
      </Typography>

      <Box
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          textAlign: 'center',
          mb: 2,
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover'
          }
        }}
      >
        <input
          type="file"
          multiple
          accept="audio/*"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          id="audio-file-input"
        />
        <label htmlFor="audio-file-input">
          <Button variant="contained" color="secondary" component="span">
            Choose Audio Files
          </Button>
        </label>
        <Typography variant="body2" sx={{ mt: 1 }}>
          or drag and drop audio files here
        </Typography>
      </Box>

      {uploadedFiles.length > 0 && (
        <Box>
          <Typography variant="subtitle2">Uploaded Files:</Typography>
          {uploadedFiles.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                backgroundColor: currentFileIndex === index ? 'action.selected' : 'transparent'
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, mr: 1 }}>
                {file.name}
              </Typography>

              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {currentFileIndex === index && isPlaying ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={pauseFile}
                    sx={{
                      borderColor: 'divider',
                      color: 'text.primary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    ⏸️ Pause
                  </Button>
                ) : currentFileIndex === index && isPaused ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={resumeFile}
                    sx={{
                      borderColor: 'divider',
                      color: 'text.primary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    ▶️ Resume
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => playFile(index)}
                    sx={{
                      borderColor: 'divider',
                      color: 'text.primary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    ▶️ Play
                  </Button>
                )}

                {currentFileIndex === index && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={stopFile}
                    sx={{
                      borderColor: 'divider',
                      color: 'text.primary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    ⏹️ Stop
                  </Button>
                )}

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => removeFile(index)}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  ×
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Audio History Window */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">Audio Playback History</Typography>
          {audioHistory.length > 0 && (
            <Button size="small" variant="outlined" onClick={() => setAudioHistory([])}>
              Clear History
            </Button>
          )}
        </Box>

        {audioHistory.length === 0 ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            No audio files have been played yet.
          </Typography>
        ) : (
          <Box sx={{
            maxHeight: '200px',
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.paper'
          }}>
            {audioHistory.map((entry, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  borderBottom: index < audioHistory.length - 1 ? '1px solid #eee' : 'none',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Typography variant="body2" sx={{ mr: 1, fontSize: '0.9rem' }}>
                    {getStatusIcon(entry.status)}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem' }}>
                    {entry.fileName}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                  {formatTime(entry.playTime)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const LiveMic: React.FC<{ context: any }> = ({ context }) => {
  const { darkMode } = useTheme();
  const [state, setState] = useState<LiveMicState>({
    record: false,
    pause: false,
    saveAudio: false,
    noSampling: false,
  });

  const predictUrl = useRef<string>('');
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const predictionReadyRef = useRef<boolean>(true);
  const recordingInterval = useRef<NodeJS.Timeout | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasCtxRef = useRef<CanvasRenderingContext2D | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const chunkCount = useRef<number>(0);
  const isRecording = useRef<boolean>(false);
  const saveAudioRef = useRef<boolean>(false);
  const noSamplingRef = useRef<boolean>(false);
  const windowSize = 3; // 3 chunks = 3 seconds total

  useEffect(() => {
    const org = context?.profile?.org !== 'master' ? context?.profile?.org : 'fmv';
    
    // For Live page, use organization-specific API in production
    if (isProd() && org) {
      const { protocol } = window.location;
      const apiHost = `${protocol}//api.${org}.safereaction.com`;
      const deviceId = `${org}-live`;
      predictUrl.current = `${apiHost}/v1/predict?device_id=${deviceId}`;
      sharedPredictUrl = predictUrl.current;
    } else {
      // Use standard getApiHost for all other cases
      getApiHost((apiHost: string) => {
        if (context && context.profile && context.profile.org) {
          const deviceId = `${context.profile.org}-live`;
          predictUrl.current = `${apiHost}/v1/predict?device_id=${deviceId}`;
        } else {
          // Fallback when profile is not loaded yet
          predictUrl.current = `${apiHost}/v1/predict?device_id=unknown-live`;
        }
        sharedPredictUrl = predictUrl.current;
      });
    }
  }, [context]);

  // Keep the ref in sync with state
  useEffect(() => {
    saveAudioRef.current = state.saveAudio;
    noSamplingRef.current = state.noSampling;
  }, [state.saveAudio, state.noSampling]);

  const startVisualization = useCallback(async () => {
    if (!canvasRef.current || !stream.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvasCtxRef.current = ctx;

    // Create shared audio context if it doesn't exist
    if (!sharedAudioContext) {
      sharedAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      sharedDestination = sharedAudioContext.createMediaStreamDestination();
    }

    // Resume audio context if suspended
    if (sharedAudioContext.state === 'suspended') {
      await sharedAudioContext.resume();
    }

    // Create a completely separate audio context for visualization
    const vizAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    await vizAudioContext.resume();

    // Clone the stream for visualization to avoid conflicts with MediaRecorder
    const vizStream = stream.current.clone();

    // Create a separate analyzer specifically for visualization
    const analyser = vizAudioContext.createAnalyser();

    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    analyser.minDecibels = -90;
    analyser.maxDecibels = -10;

    // Connect analyzer to the shared destination (mixed audio) instead of just microphone
    if (sharedDestination) {
      const mixedSource = vizAudioContext.createMediaStreamSource(sharedDestination.stream);
      mixedSource.connect(analyser);
    } else {
      // Fallback to microphone only if shared destination doesn't exist yet
      const source = vizAudioContext.createMediaStreamSource(vizStream);
      source.connect(analyser);
    }

    // Create a separate source for the shared destination (for recording)
    const recordingSource = sharedAudioContext.createMediaStreamSource(stream.current);
    recordingSource.connect(sharedDestination);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    // Add a small delay to allow the analyzer to start receiving data
    await new Promise(resolve => setTimeout(resolve, 200));

    const draw = () => {
      if (!canvasCtxRef.current) return;

      animationFrameRef.current = requestAnimationFrame(draw);

      // Get frequency data for visualization
      analyser.getByteFrequencyData(dataArray);

      const ctx = canvasCtxRef.current;
      const width = canvas.width;
      const height = canvas.height;

      ctx.clearRect(0, 0, width, height);
      // Use transparent background
      // No background fill needed for transparency

      const barWidth = (width / bufferLength) * 2.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        barHeight = dataArray[i];
        ctx.fillStyle = 'red';
        ctx.fillRect(x, height - barHeight / 2, barWidth, barHeight / 2);
        x += barWidth + 1;
      }
    };

    draw();
  }, []);

  const stopVisualization = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (canvasCtxRef.current) {
      canvasCtxRef.current.clearRect(0, 0, 640, 100);
    }
  }, []);

  const handleSaveAudioChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, v: boolean) => {
    saveAudioRef.current = v;
    setState(prev => ({ ...prev, saveAudio: v }));
  }, []);

  const handleNoSamplingChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, v: boolean) => {
    noSamplingRef.current = v;
    setState(prev => ({ ...prev, noSampling: v }));
  }, []);

  const makePrediction = useCallback(async (audioBlob: Blob) => {
    // Send the WebM blob directly without conversion
    const formData = new FormData();
    formData.append('audio_file', audioBlob);
    const fetchInit = {
      credentials: 'include' as RequestCredentials,
      method: 'POST',
      body: formData,
    };

    let apiUrl = predictUrl.current;
    // Use the ref to get the current saveAudio value
    if (saveAudioRef.current) {
      apiUrl += '&class_label=live&save_audio=true&lazy_saving=false';
    }
    if (noSamplingRef.current) {
      apiUrl += '&no_sampling=true';
    }

    const callback = (fetch: Promise<Response>) => fetch
      .then((res) => res.json())
      .then((res: { events: EventPrediction[] }) => {
        predictionReadyRef.current = true;
        eventPredictions.addEventPredictions(res.events);
      })
      .catch((e: any) => {
        console.error('Prediction error:', e);
        predictionReadyRef.current = true;
      });

    fetchApiRequest(apiUrl, callback, fetchInit);
  }, []);

  const createSlidingWindow = useCallback(() => {
    // Only create window if we have enough chunks
    if (audioChunks.current.length < windowSize) {
      return null;
    }

    // Get the last 'windowSize' chunks for the sliding window
    const windowChunks = audioChunks.current.slice(-windowSize);

    // Check if all chunks in the window are large enough
    const validChunks = windowChunks.filter(chunk => chunk.size > 2000);

    if (validChunks.length < windowSize) {
      return null;
    }

    // Combine chunks into a single blob
    const combinedBlob = new Blob(validChunks, { type: 'audio/webm;codecs=opus' });

    return combinedBlob;
  }, []);

  const startNewRecording = useCallback(async () => {
    if (!stream.current) return;

    // Stop existing recorder if any
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }

    // Use shared destination for recording if available (for mixing)
    const recordingStream = sharedDestination ? sharedDestination.stream : stream.current;

    // Create new recorder
    const recorder = new MediaRecorder(recordingStream, {
      mimeType: 'audio/webm;codecs=opus',
      audioBitsPerSecond: 64000
    });

    mediaRecorder.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunkCount.current++;

        // Only add large chunks to the buffer
        if (event.data.size > 200) {
          audioChunks.current.push(event.data);

          // Keep only the last few chunks to manage memory
          if (audioChunks.current.length > windowSize + 1) {
            audioChunks.current = audioChunks.current.slice(-windowSize - 1);
          }

          // Try to create a sliding window for prediction
          const windowBlob = createSlidingWindow();
          if (windowBlob && predictionReadyRef.current) {
            predictionReadyRef.current = false;
            makePrediction(windowBlob);
          } else if (!windowBlob) {
            predictionReadyRef.current = true;
          }
        } else {
          predictionReadyRef.current = true;
        }
      }
    };

    recorder.start(2000); // Record for 2 second
  }, [makePrediction, createSlidingWindow]);

  const startRecording = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          autoGainControl: false,
          noiseSuppression: false,
          channelCount: 1,
          sampleRate: 12000,
        },
        video: false
      });

      stream.current = mediaStream;
      audioChunks.current = [];
      chunkCount.current = 0;
      isRecording.current = true;

      // Ensure audio context is resumed before starting visualization
      if (sharedAudioContext && sharedAudioContext.state === 'suspended') {
        await sharedAudioContext.resume();
      }

      // Start the first recording
      await startNewRecording();

      // Start periodic recordings (predictions happen immediately when chunks are ready)
      recordingInterval.current = setInterval(() => {
        if (isRecording.current) {
          startNewRecording();
        }
      }, 1000); // Restart recording every 1 second

      await startVisualization();
      setState(prev => ({ ...prev, record: true, pause: false }));

    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to start recording. Please check microphone permissions.');
    }
  }, [startNewRecording, startVisualization]);

  const stopRecording = useCallback(() => {
    if (recordingInterval.current) {
      clearInterval(recordingInterval.current);
    }

    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }

    if (stream.current) {
      stream.current.getTracks().forEach(track => track.stop());
      stream.current = null;
    }

    stopVisualization();
    setState(prev => ({ ...prev, record: false, pause: false }));
  }, [stopVisualization]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingInterval.current) {
        clearInterval(recordingInterval.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (stream.current) {
        stream.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const buttonStyle = { marginRight: '1rem' };
  const { record, pause, saveAudio, noSampling } = state;

  return (
    <Paper style={{ maxWidth: '42rem' }}>
      <Typography variant="subtitle1">Live audio recording</Typography>
      <Typography variant="body2">Note: this is a beta function only for
      experimental purposes. The model has not been trained for the
      microphone in the computer, hence the performance will be poor.
      </Typography>
      <div>
        <Button
          style={buttonStyle}
          onClick={startRecording}
          disabled={record}
          variant="contained"
          color="secondary"
        >
          Record
        </Button>
        <Button
          style={buttonStyle}
          onClick={stopRecording}
          disabled={!record}
          variant="contained"
          color="secondary"
        >
          STOP
        </Button>
        <FormControlLabel
          labelPlacement="start"
          style={{ height: '1.8rem', marginLeft: '0.5rem' }}
          control={
            <Switch
              checked={saveAudio}
              onChange={handleSaveAudioChange}
              color="primary"
              name="saveAudio"
              style={{ bottom: '1px' }}
            />
          }
          label="Save Audio"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontWeight: saveAudio ? 'bold' : 'normal'
            }
          }}
        />
        <FormControlLabel
          labelPlacement="start"
          style={{ height: '1.8rem', marginLeft: '1.5rem' }}
          control={
            <Switch
              checked={noSampling}
              onChange={handleNoSamplingChange}
              color="primary"
              name="noSampling"
              style={{ bottom: '1px' }}
            />
          }
          label="No Sampling"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontWeight: noSampling ? 'bold' : 'normal'
            }
          }}
        />
      </div>
      <canvas
        ref={canvasRef}
        width={640}
        height={100}
        style={{
          backgroundColor: 'transparent'
        }}
      />
    </Paper>
  );
};

export default withRouter(Live);